"use client";

import { TypeBannerSkeleton } from "@contentful/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendGTMEvent } from "@next/third-parties/google";
import { GtmEvents } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@yardzen/ui/components/form";
import { Icon } from "@yardzen/ui/components/icon";
import { Input } from "@yardzen/ui/components/input";
import { Spinner } from "@yardzen/ui/components/spinner";
import { Entry } from "contentful";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { RichText } from "../../../../next-components/src/contentful/rich-text/RichText";
import { isCustomerLeadEmailNew } from "../../../email-capture-form/src/lib/actions";
import { createCustomerLeadFromModal } from "../../../lead-capture-modal/src/lib/createCustomerLead";

function shouldShowBanner(
  currentPath: string,
  restrictedURLs: string[] | undefined,
): boolean {
  // Check if current path is in restricted URLs
  if (restrictedURLs && restrictedURLs.length > 0) {
    const isPathRestricted = restrictedURLs.some((restrictedURL) => {
      // Handle exact matches and path prefixes
      return (
        currentPath === restrictedURL || currentPath.startsWith(restrictedURL)
      );
    });
    if (isPathRestricted) {
      return false;
    }
  }

  // Check for 'yz-banner-dismissed' cookie that is set on banner dismissal
  if (typeof window !== "undefined") {
    if (
      document.cookie
        .split(";")
        .some((cookie) => cookie.trim().startsWith("yz-banner-dismissed="))
    ) {
      return false;
    }
  }

  return true;
}

export function LeadCaptureBanner({
  contentfulContent,
}: {
  contentfulContent: Entry<
    TypeBannerSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  > | null;
}) {
  const path = usePathname();
  const [show, setShow] = useState<boolean | null>(null); // null = not yet checked
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const restrictedURLs = contentfulContent?.fields.restrictedUrLs;

  // Check cookie and path restrictions on mount and when path changes
  useEffect(() => {
    const shouldShow = shouldShowBanner(path, restrictedURLs);
    setShow(shouldShow);
  }, [path, restrictedURLs]);

  const handleDismiss = () => {
    // Set cookie to remember dismissal
    if (typeof window !== "undefined") {
      document.cookie = "yz-banner-dismissed=true; path=/";
    }
    setShow(false);
  };

  // Don't render until we've checked the cookie (prevents flicker)
  if (show === null) {
    return null;
  }

  // Don't render if banner should not be shown
  if (!show) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50 w-full">
      <div className="relative flex w-full flex-col items-center justify-start gap-4 bg-[#ECF4BF] px-8 py-2 shadow-md sm:justify-center md:h-16 md:flex-row">
        {emailSubmitted ? (
          <BannerSuccess
            contentfulContent={contentfulContent}
            onDismiss={handleDismiss}
          />
        ) : (
          <EmailInput
            onSuccess={() => setEmailSubmitted(true)}
            contentfulContent={contentfulContent}
          />
        )}
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-2 flex-shrink-0 md:relative md:right-auto md:top-auto md:ml-auto"
          aria-label="Close banner"
        >
          <Icon name="xmark" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function EmailInput({
  onSuccess,
  contentfulContent,
}: {
  onSuccess: () => void;
  contentfulContent: Entry<
    TypeBannerSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  > | null;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const leadCaptureSchema = z.object({
    email: z.string().email("Invalid email address"),
  });

  const form = useForm({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(leadCaptureSchema),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Validate form first
    const isValid = await form.trigger();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      const email = form.getValues("email");
      const isNewLead = await isCustomerLeadEmailNew(email);
      const formData = new FormData();
      formData.append("email", email);
      await createCustomerLeadFromModal(null, formData, "banner");

      sendGTMEvent({
        event: GtmEvents.GENERATE_LEAD,
        new_design_quiz_lead: isNewLead ? 1 : 0,
      });

      setIsSubmitting(false);
      // Show success screen on successful submission
      onSuccess();
    } catch (error) {
      console.error("Failed to submit form:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex flex-shrink-0 flex-row items-center justify-start gap-2">
        <Icon name="sparkles" size="sm" />
        {contentfulContent?.fields.bodyCopy && (
          <RichText
            content={contentfulContent?.fields.bodyCopy}
            pClassName="!mb-0 sm:text-base text-sm !leading-normal sm:whitespace-nowrap"
          />
        )}
      </div>
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-row items-center gap-1 sm:w-auto"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="max-w-[75%] flex-1 sm:max-w-none sm:flex-none">
                <FormControl>
                  <Input
                    placeholder="Enter your email"
                    type="email"
                    {...field}
                    className="h-8 w-full text-sm sm:h-10 sm:w-64 sm:text-base"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="flex-shrink-0"
            size="sm"
            disabled={!form.formState.isValid || isSubmitting}
          >
            {isSubmitting ? (
              <Spinner className="size-4" />
            ) : (
              contentfulContent?.fields.buttonText || "Submit Email"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}

function BannerSuccess({
  contentfulContent,
  onDismiss,
}: {
  contentfulContent: Entry<
    TypeBannerSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  > | null;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="flex w-full flex-row items-center gap-4">
      <Icon name="circle-check" size="md" />

      {contentfulContent?.fields.successCopy && (
        <RichText
          content={contentfulContent?.fields.successCopy}
          pClassName="!mb-0 sm:text-base text-sm !leading-normal"
        />
      )}
    </div>
  );
}
